import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Table, Checkbox, Button, Space, Popover, Divider } from 'antd'
import { ColumnType } from 'antd/lib/table'
import { format, setMonth } from 'date-fns'
import dayjs from 'dayjs'
import SortableList, { SortableItem as EasySortItem, SortableKnob } from 'react-easy-sort'
import arrayMove from 'array-move'
import IntlMessages from '../../util/IntlMessages'
import { IColumnProps, IReportTableProps, reportResultsLimit } from './types'
import { DateFormat, Display } from '@vacationtracker/shared/types/export'
import { IQuotaAmount } from '@vacationtracker/shared/types/leave-policy'

const SortableItem = ({ column, onCheckboxChange }: { column: IColumnProps; onCheckboxChange: (key: string, checked: boolean) => void }) => {
  return (
    <EasySortItem>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 8,
          padding: '4px',
          borderRadius: '4px',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
      >
        <SortableKnob>
          <span
            style={{
              cursor: 'grab',
              marginRight: 8,
              fontSize: '16px',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
          >
            ☰
          </span>
        </SortableKnob>
        <Checkbox checked={column.visible} onChange={(e) => onCheckboxChange(column.key, e.target.checked)}>
          {column.title}
        </Checkbox>
      </div>
    </EasySortItem>
  )
}

const SortableBody = ({
  items,
  onCheckboxChange,
  onSortEnd,
}: {
  items: IColumnProps[]
  onCheckboxChange: (key: string, checked: boolean) => void
  onSortEnd: (oldIndex: number, newIndex: number) => void
}) => {
  return (
    <SortableList onSortEnd={onSortEnd} className="sortable-column-list" draggedItemClassName="dragged-column-item" allowDrag>
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {items.map((column) => (
          <SortableItem key={column.key} column={column} onCheckboxChange={onCheckboxChange} />
        ))}
      </div>
    </SortableList>
  )
}

const ReportTable = ({
  columnNames,
  data,
  total,
  loading,
  page,
  dateFormat,
  display,
  reportType,
  editableColumns,
  hideEmptyColumns,
  onHideEmptyColumnsChange,
  loadNext,
  loadPrevious,
  download,
}: IReportTableProps) => {
  const { formatMessage } = useIntl()
  const initialColumns: IColumnProps[] = columnNames.map((col) => ({
    title: col.startsWith('month-')
      ? formatMessage({ id: 'components.tables.month' }, { month: format(setMonth(new Date(), Number(col.split('-')[1]) - 1), 'MMM'), year: col.split('-')[2] })
      : formatMessage({ id: `components.tables.${col}` }),
    dataIndex: col,
    key: col,
    visible: true,
    render: (val: string | boolean) => {
      if (val === null || val === '') {
        return 'N/A'
      }
      if (typeof val === 'boolean') {
        return val ? <IntlMessages id="app.yes" /> : <IntlMessages id="app.no" />
      }
      return val
    },
    ...(col === 'user' && {
      render: (text: string, record: Record<string, unknown>) => (
        <a href={`/app/users/${record.userId}`} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    }),
  }))

  const formatData = (data: Record<string, any>[], format: DateFormat, display: Display) => {
    return data.map((item) => {
      const formattedItem = {
        ...item,
        startDate: dayjs(item.startDate as string).format(format),
        endDate: dayjs(item.endDate as string).format(format),
      }
      for (const key in item) {
        if (item[key] && typeof item[key] === 'object' && 'days' in item[key] && 'hours' in item[key]) {
          formattedItem[key] = display === 'days' ? (item[key] as IQuotaAmount).raw.days : (item[key] as IQuotaAmount).raw.hours
          if ((item[key] as IQuotaAmount).raw?.days === Number.MAX_VALUE) {
            formattedItem[key] = '∞'
          }
        }
      }
      return formattedItem
    })
  }

  const loadColumnSettings = (): { columns: { key: string; visible: boolean }[]; columnOrder: string[] } | null => {
    const settings = localStorage.getItem(`${reportType}TableSettings`)
    if (settings) {
      try {
        return JSON.parse(settings)
      } catch (error) {
        console.error('Failed to parse column settings from localStorage:', error)
      }
    }
    return null
  }

  const saveColumnSettings = (columns: IColumnProps[], columnOrder: string[]) => {
    const settings = {
      columns: columns.map(({ key, visible }) => ({ key, visible })),
      columnOrder,
    }
    localStorage.setItem(`${reportType}TableSettings`, JSON.stringify(settings))
  }

  const savedSettings = loadColumnSettings()
  const initialColumnState = savedSettings
    ? savedSettings.columnOrder
        .map((key) => {
          const col = initialColumns.find((c) => c.key === key)
          if (!col) return null
          return {
            ...col,
            visible: savedSettings.columns.find((c) => c.key === key)?.visible ?? true,
          }
        })
        .filter((col): col is IColumnProps => col !== null)
    : initialColumns.map((col) => ({ ...col, visible: true }))

  const initialColumnOrder = savedSettings ? savedSettings.columnOrder : initialColumns.map((col) => col.key)

  const [columns, setColumns] = useState(initialColumnState)
  const [columnOrder, setColumnOrder] = useState(initialColumnOrder)
  const [popoverColumns, setPopoverColumns] = useState(initialColumnState)

  const handleCheckboxChange = (key: string, checked: boolean) => {
    const updatedColumns = columns.map((col) => (col.key === key ? { ...col, visible: checked } : col))
    setColumns(updatedColumns)
    setPopoverColumns(updatedColumns)
    saveColumnSettings(updatedColumns, columnOrder)
  }

  const handleSelectAll = () => {
    const updatedColumns = columns.map((col) => ({ ...col, visible: true }))
    setColumns(updatedColumns)
    setPopoverColumns(updatedColumns)
    saveColumnSettings(updatedColumns, columnOrder)
  }

  const handleUnselectAll = () => {
    const updatedColumns = columns.map((col, index) => ({
      ...col,
      visible: index === 0, // Only the first column remains visible
    }))
    setColumns(updatedColumns)
    setPopoverColumns(updatedColumns)
    saveColumnSettings(updatedColumns, columnOrder)
  }

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const newOrder = arrayMove(columnOrder, oldIndex, newIndex)
    setColumnOrder(newOrder)

    const reorderedColumns = newOrder.map((key) => columns.find((col) => col.key === key))
    setColumns(reorderedColumns as IColumnProps[])
    setPopoverColumns(reorderedColumns as IColumnProps[])
    saveColumnSettings(reorderedColumns as IColumnProps[], newOrder)
  }

  const formattedData = useMemo(() => formatData(data as Record<string, unknown>[], dateFormat, display), [data, dateFormat, display])

  const emptyColumnKeys = useMemo(() => {
    if (data.length === 0) return new Set<string>()
    const keys = new Set<string>()
    for (const col of columns) {
      const allEmpty = formattedData.every((row) => {
        const val = row[col.key]
        return val === null || val === undefined || val === ''
      })
      if (allEmpty) keys.add(col.key)
    }
    return keys
  }, [formattedData, columns, data.length])

  useEffect(() => {
    if (hideEmptyColumns !== undefined) {
      localStorage.setItem(`${reportType}_emptyColumnKeys`, JSON.stringify(Array.from(emptyColumnKeys)))
      localStorage.setItem(`${reportType}_allColumnKeys`, JSON.stringify(columns.map((col) => col.key)))
    }
  }, [emptyColumnKeys, reportType, hideEmptyColumns, columns])

  const filtredColumns = columns.filter((col) => col?.visible).filter((col) => !(hideEmptyColumns && emptyColumnKeys.has(col.key)))

  return (
    <div id="report-table-container">
      <Space style={{ marginBottom: 16 }}></Space>

      <div id="report-table-btns-container">
        <Button disabled={total === 0} onClick={download} style={{ margin: '5px' }}>
          <IntlMessages id="app.download" />
        </Button>
        {editableColumns && (
          <Popover
            title={<IntlMessages id="components.tables.editColumnsDesc" />}
            trigger="click"
            placement="bottom"
            arrowPointAtCenter
            content={
              <div>
                <Button onClick={handleSelectAll} style={{ marginBottom: '8px', width: '100%' }}>
                  <IntlMessages id="createCompany.steps.selectUsers.selectAll" />
                </Button>
                <Button onClick={handleUnselectAll} style={{ marginBottom: '8px', width: '100%' }}>
                  <IntlMessages id="createCompany.steps.selectUsers.deselectAll" />
                </Button>
                <SortableBody items={popoverColumns} onSortEnd={onSortEnd} onCheckboxChange={handleCheckboxChange} />
                {onHideEmptyColumnsChange && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Checkbox checked={hideEmptyColumns} onChange={(e) => onHideEmptyColumnsChange(e.target.checked)}>
                      <IntlMessages id="components.reports.hideEmptyColumns" />
                    </Checkbox>
                  </>
                )}
              </div>
            }
          >
            <Button style={{ margin: '5px' }}>
              <IntlMessages id="components.tables.editColumns" />
            </Button>
          </Popover>
        )}
      </div>

      <Table
        columns={filtredColumns as ColumnType<Record<string, unknown>>[]}
        dataSource={formattedData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={loading}
        showHeader={true}
        sticky={true}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
        <div>
          <IntlMessages id={`app.results${reportType === 'leave-history-report' ? 'Leaves' : 'Users'}`} values={{ total }} />
        </div>
        <div>
          <Button className="prev-next-btns" disabled={loading || page === 1} onClick={loadPrevious} style={{ width: '100px' }}>
            <IntlMessages id="app.previous" />
          </Button>
          <Button
            className="prev-next-btns"
            disabled={loading || data.length < reportResultsLimit || data.length === total}
            onClick={loadNext}
            style={{ width: '100px', marginLeft: '10px' }}
          >
            <IntlMessages id="app.next" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ReportTable
